import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../../services/api';
import type { Completeness, PersonaProgress } from '../../../services/api';
import { getStoredSessionId, resolveCurrentSessionId } from '../../../services/currentSession';

/**
 * Coordinators see no score until every persona has answered every assigned question.
 * Until then they see per-persona progress (Pat's D3, option B).
 *
 * Fails CLOSED: while loading, and when the check errors, the children (the score page) are
 * not mounted. The score endpoints answer 409 while incomplete, so mounting the page early
 * would only produce an error screen.
 *
 * Status is written as text on every row -- the user is colour blind.
 */

type State =
  | { kind: 'loading' }
  | { kind: 'error'; sessionId: string }
  | { kind: 'ready'; sessionId: string; completeness: Completeness };

function statusOf(p: PersonaProgress): 'Not started' | 'Complete' | 'In progress' {
  if (p.answered === 0) return 'Not started';
  if (p.total > 0 && p.answered >= p.total) return 'Complete';
  return 'In progress';
}

// The payload decides whether a score page mounts, so a shape we don't recognise is an error
// (fail closed), never a render crash and never "complete".
function isCompleteness(value: unknown): value is Completeness {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.complete === 'boolean' && Array.isArray(v.perPersona) && typeof v.personasTotal === 'number';
}

function CheckingPanel() {
  return (
    <div className="p-8 text-center text-gray-600" role="status" aria-live="polite">
      Checking assessment progress...
    </div>
  );
}

function CheckFailedPanel() {
  return (
    <div className="mx-auto my-8 max-w-xl rounded-lg border border-gray-300 bg-white p-6 text-center" role="alert">
      <h2 className="text-lg font-semibold text-gray-900">We couldn&apos;t check assessment progress</h2>
      <p className="mt-2 text-sm text-gray-600">Scores stay hidden until we can confirm every persona has finished. Please try again.</p>
    </div>
  );
}

const STATUS_STYLE = {
  'Not started': 'border-gray-400 text-gray-700',
  'In progress': 'border-amber-600 text-amber-800',
  Complete: 'border-emerald-700 text-emerald-800',
} as const;

export function AssessmentProgressGate({
  sessionId,
  children,
}: {
  sessionId: string | null;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    apiService
      .getAssessmentCompleteness(sessionId)
      .then((completeness: unknown) => {
        if (cancelled) return;
        if (isCompleteness(completeness)) setState({ kind: 'ready', sessionId, completeness });
        else setState({ kind: 'error', sessionId });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error', sessionId });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (!sessionId) return <>{children}</>;

  // A result for a previous session id never counts for this one (fail closed on id change).

  if (state.kind === 'loading' || state.sessionId !== sessionId) return <CheckingPanel />;

  if (state.kind === 'error') return <CheckFailedPanel />;

  const { completeness } = state;
  if (completeness.complete) return <>{children}</>;

  if (completeness.personasTotal === 0) {
    return (
      <div className="mx-auto my-8 max-w-xl rounded-lg border border-gray-300 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">This assessment has no participants set up yet</h2>
        <p className="mt-2 text-sm text-gray-600">
          Scores appear once participants are invited and every one has finished.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto my-8 max-w-xl rounded-lg border border-gray-300 bg-white p-6">
      <h2 className="text-lg font-semibold text-gray-900">
        Scores appear when all {completeness.personasTotal} personas finish
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Results stay hidden until every persona has answered, so no partial score reaches your board.
      </p>
      <ul className="mt-4 divide-y divide-gray-200">
        {completeness.perPersona.map((p) => {
          const status = statusOf(p);
          return (
            <li key={p.personaCode} className="flex items-center justify-between gap-4 py-2">
              <span className="text-sm text-gray-900">
                {p.personaName} {p.answered}/{p.total}
              </span>
              <span className={`rounded border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status]}`}>
                {status}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type Resolution = { kind: 'resolving' } | { kind: 'error' } | { kind: 'done'; sessionId: string | null };

/**
 * Resolves the session for a route so App.jsx stays declarative.
 *
 * source="localStorage" with nothing stored (every sign-in clears activeSessionId, and the
 * dashboard that would store it never mounts for an incomplete session) resolves the caller's
 * latest session first -- the same one Compass/Defense would resolve for themselves -- and gates
 * on it. Children pass through only when there is genuinely no session; a failed resolution is
 * the error panel (fail closed).
 */
export function RouteSessionGate({
  source,
  children,
}: {
  source: 'param' | 'localStorage';
  children: React.ReactNode;
}) {
  const params = useParams();
  const stored = source === 'param' ? params.sessionId ?? null : getStoredSessionId();
  const needsResolve = source === 'localStorage' && !stored;
  const [resolution, setResolution] = useState<Resolution>({ kind: 'resolving' });

  useEffect(() => {
    if (!needsResolve) {
      setResolution({ kind: 'resolving' }); // a later resolution never starts from a stale one
      return undefined;
    }
    let cancelled = false;
    setResolution({ kind: 'resolving' });
    resolveCurrentSessionId().then(
      (sessionId) => {
        if (!cancelled) setResolution({ kind: 'done', sessionId });
      },
      () => {
        if (!cancelled) setResolution({ kind: 'error' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needsResolve]);

  if (!needsResolve) return <AssessmentProgressGate sessionId={stored}>{children}</AssessmentProgressGate>;
  if (resolution.kind === 'resolving') return <CheckingPanel />;
  if (resolution.kind === 'error') return <CheckFailedPanel />;
  return <AssessmentProgressGate sessionId={resolution.sessionId}>{children}</AssessmentProgressGate>;
}
